import React, { useState, useRef, useEffect, useCallback } from 'react';
import { usePaperTradingStore } from '../store/paperTradingStore';
import { useMarketStore } from '../store/marketStore';
import { useBrokerStore, brokerSelectors } from '../store/brokerStore';
import { useDisciplineStore } from '../store/disciplineStore';
import { addLog } from '../store/liveLogStore';
import { useToastStore, addToast } from '../store/toastStore';
import { usePreferences } from '../hooks/usePreferences';
import { useDefaultQuantity } from '../hooks/useDefaultQuantity';
import { clampOrderQuantity, sanitizeOrderQuantity } from '../config/orderQuantity';
import { useDraggable } from '../hooks/useDraggable';
import { useIsMobile } from '../hooks/useIsMobile';
import { isFullSizeContract } from './ActionBarTrading/useTradingLogic';
import ScheduleSelector from './ActionBarTrading/ScheduleSelector';
import { useScheduledOrderStore, resolveFireTime } from '../store/scheduledOrderStore';
import { useAutoBotGlobalEnabled, useAutoBotStore } from '../store/autoBotStore';
import { getInstrumentSpec, isTradeable, isAlwaysOpen } from '../config/instruments';
import FullSizeContractConfirmModal from './FullSizeContractConfirmModal';
import { ChevronLeft, ChevronRight, Minus, Plus, Zap, TrendingUp, TrendingDown, X, Shield } from 'lucide-react';
import { TradingLockButton } from './ActionBarTrading/TradingLockButton';
import { useNewsProximity } from '../hooks/useNewsProximity';
import NewsOrderConfirmModal from './NewsOrderConfirmModal';
import { AnimatedRainbowBorder } from './AnimatedRainbowBorder';
import { RestrainToggle, useRestrainOrderLock } from './Restrain';
import { findOpenPosition, manualOrderButton, planManualOrder, serverSizedLegs } from '../utils/exitOnly';
import './TradingPanel.css';

/**
 * CSS custom property carrying the mobile trading bar's measured height.
 * Set on <html> by the bar itself; read by ChartView.css (chart padding) and
 * TradingPanel.css (settings sheet offset). Absent means "no bar rendered".
 */
const MOBILE_BAR_HEIGHT_VAR = '--mobile-trading-bar-h';

interface TradingPanelProps {
  /** Optional symbol override for multi-chart mode */
  symbol?: string;
  /** Optional current price override for multi-chart mode */
  currentPrice?: number;
}

const TradingPanel: React.FC<TradingPanelProps> = ({ symbol: propSymbol, currentPrice: propCurrentPrice }) => {
  const isMobile = useIsMobile();
  const botsArmed = useAutoBotGlobalEnabled();

  // Queue the order when a schedule is set, otherwise run it now. Wraps the
  // SAME execution path, so side, quantity and TP/SL are identical either way.
  const scheduleOrRun = useCallback((action: 'Buy' | 'Sell', run: () => void) => {
    const st = useScheduledOrderStore.getState();
    if (st.mode === 'now') {
      run();
      return;
    }
    const resolved = resolveFireTime(st.mode, Date.now(), 2, st.leadSeconds, st.atTime);
    // Unschedulable (a time already past) — refuse rather than fire instantly.
    if (!resolved) return;
    st.setPending({ action, fireAt: resolved.fireAt, label: resolved.label });
  }, []);

  // Run the queued order through the same path a tap takes.
  useEffect(() => {
    const onFire = (e: Event): void => {
      const action = (e as CustomEvent).detail as 'Buy' | 'Sell';
      if (action === 'Buy') checkNewsAndExecute('Buy', handleBuy);
      else checkNewsAndExecute('Sell', handleSell);
    };
    window.addEventListener('scheduled-order-fire', onFire);
    return () => window.removeEventListener('scheduled-order-fire', onFire);
  });

  // Fire a queued order when its moment arrives. Declared here with the other
  // hooks, ABOVE any early return — a conditional hook is what crashed the
  // chart on prod earlier today.
  useEffect(() => {
    const id = setInterval(() => {
      const st = useScheduledOrderStore.getState();
      const p = st.pending;
      if (!p || Date.now() < p.fireAt) return;
      st.setPending(null);
      window.dispatchEvent(new CustomEvent('scheduled-order-fire', { detail: p.action }));
    }, 1000);
    return () => clearInterval(id);
  }, []);
  const { preferences, updatePreference, isLoading: prefsLoading } = usePreferences();
  const tradingPrefs = preferences.tradingPanel || {};

  // Get size based on device type (mobile vs desktop)
  const getInitialSize = () => {
    if (isMobile && tradingPrefs.mobileSize) {
      return tradingPrefs.mobileSize;
    }
    return tradingPrefs.size ?? { width: 120, height: 0 };
  };

  // Shared with desktop order entry (useTradingLogic) — one stored default.
  const { defaultQuantity, quantityPresets } = useDefaultQuantity();

  const [selectedQuantity, setSelectedQuantity] = useState<number>(
    sanitizeOrderQuantity(tradingPrefs.quantity, defaultQuantity),
  );
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [isResizing, setIsResizing] = useState<boolean>(false);
  const [size, setSize] = useState<{ width: number; height: number }>(getInitialSize());
  const [useOCO, setUseOCO] = useState<boolean>(tradingPrefs.useOCO ?? true);
  const [ocoTPEnabled, setOcoTPEnabled] = useState<boolean>(tradingPrefs.ocoTPEnabled ?? true);
  const [ocoSLEnabled, setOcoSLEnabled] = useState<boolean>(tradingPrefs.ocoSLEnabled ?? true);
  const [ocoTPOffset, setOcoTPOffset] = useState<number>(tradingPrefs.ocoTPOffset ?? 6);
  const [ocoSLOffset, setOcoSLOffset] = useState<number>(tradingPrefs.ocoSLOffset ?? 50);
  const panelRef = useRef<HTMLDivElement>(null);
  const hasSyncedPrefs = useRef(false);

  // Sync from server preferences when they load (one-time)
  // Only sync when we have actual trading settings (not just position data)
  useEffect(() => {
    if (!prefsLoading && preferences.tradingPanel && !hasSyncedPrefs.current) {
      const tp = preferences.tradingPanel;
      // Check if we have actual trading settings, not just position/size from partial load
      const hasActualSettings = tp.useOCO !== undefined || tp.ocoTPOffset !== undefined ||
                                tp.ocoSLOffset !== undefined || tp.quantity !== undefined ||
                                tp.defaultQuantity !== undefined;

      if (hasActualSettings) {
        hasSyncedPrefs.current = true;
        console.log('[TradingPanel] Syncing from server preferences:', tp);
        // No stored working size yet? Start at the default rather than at 1.
        setSelectedQuantity(sanitizeOrderQuantity(tp.quantity, defaultQuantity));
        if (tp.useOCO !== undefined) setUseOCO(tp.useOCO);
        if (tp.ocoTPEnabled !== undefined) setOcoTPEnabled(tp.ocoTPEnabled);
        if (tp.ocoSLEnabled !== undefined) setOcoSLEnabled(tp.ocoSLEnabled);
        if (tp.ocoTPOffset !== undefined) setOcoTPOffset(tp.ocoTPOffset);
        if (tp.ocoSLOffset !== undefined) setOcoSLOffset(tp.ocoSLOffset);
        // Use device-specific size
        if (isMobile && tp.mobileSize !== undefined) {
          setSize(tp.mobileSize);
        } else if (tp.size !== undefined) {
          setSize(tp.size);
        }
      } else {
        console.log('[TradingPanel] Skipping sync - no trading settings in preferences yet:', tp);
      }
    }
  }, [prefsLoading, preferences.tradingPanel, isMobile, defaultQuantity]);

  // Listen for DCA sync quantity events
  useEffect(() => {
    const handler = (e: Event) => {
      const qty = (e as CustomEvent).detail?.quantity;
      if (typeof qty === 'number' && qty > 0) {
        setSelectedQuantity(qty);
      }
    };
    window.addEventListener('dca-sync-quantity', handler);
    return () => window.removeEventListener('dca-sync-quantity', handler);
  }, []);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Use draggable hook for panel positioning
  const { position, isDragging, dragHandlers } = useDraggable(panelRef, {
    defaultPosition: { x: 18, y: 190 },
    preferenceKey: 'tradingPanel',
    constrainToViewport: true,
    excludeSelectors: ['button', 'input', '.tp-resize-handle']
  });

  const { enabled } = usePaperTradingStore();
  const { currentPrice: marketCurrentPrice, dataMode, isMarketOpen, marketStatus, symbol: marketSymbol } = useMarketStore();

  // Use prop overrides or fall back to marketStore values
  const symbol = propSymbol || marketSymbol;
  const currentPrice = propCurrentPrice ?? marketCurrentPrice;

  // Use the new broker store for multi-broker support
  const activeBroker = brokerSelectors.useActiveBroker();
  const placeOrder = useBrokerStore(state => state.placeOrder);
  const placeBracketOrder = useBrokerStore(state => state.placeBracketOrder);
  const flattenPosition = useBrokerStore(state => state.flattenPosition);
  const replaceBracket = useBrokerStore(state => state.replaceBracket);
  const isHedgeCopyModeActive = useBrokerStore(state => state.isHedgeCopyModeActive());
  const tradingLocked = useBrokerStore(state => state.tradingLocked);
  const setTradingLock = useBrokerStore(state => state.setTradingLock);

  // Discipline store for advanced lock features
  const disciplineLockMode = useDisciplineStore(state => state.state.lockMode);
  const disciplineLockExpiresAt = useDisciplineStore(state => state.state.lockExpiresAt);
  const startCoolOff = useDisciplineStore(state => state.startCoolOff);
  const startCoolOffUntil = useDisciplineStore(state => state.startCoolOffUntil);
  const setDoneForDay = useDisciplineStore(state => state.setDoneForDay);
  const openUnlockModal = useDisciplineStore(state => state.openUnlockModal);
  const openSettingsModal = useDisciplineStore(state => state.openSettingsModal);
  const setLockMode = useDisciplineStore(state => state.setLockMode);
  const simpleUnlock = useDisciplineStore(state => state.simpleUnlock);
  const simpleUnlockEnabled = useDisciplineStore(state => state.settings.simpleUnlockEnabled);
  const isDisciplineLocked = disciplineLockMode !== 'unlocked';

  // State for lock dropdown
  const [lockDropdownOpen, setLockDropdownOpen] = useState(false);
  const [fullSizeConfirm, setFullSizeConfirm] = useState<{ action: 'Buy' | 'Sell' } | null>(null);
  const [fullSizeConfirmDisabled, setFullSizeConfirmDisabled] = useState(false);
  const fullSizeBypassRef = useRef(false);

  // Reset full-size toggle on symbol change
  const prevSymbolRef = useRef(symbol);
  useEffect(() => {
    if (prevSymbolRef.current !== symbol) {
      prevSymbolRef.current = symbol;
      setFullSizeConfirmDisabled(false);
    }
  }, [symbol]);

  // Track previous hedge mode state to detect when it's disabled
  const prevHedgeModeRef = useRef(isHedgeCopyModeActive);

  // Hedge mode: enforce TP = SL
  // When hedge copy mode is active, both TP and SL must be equal
  // When hedge mode is disabled, reset to defaults (TP=5, SL=50)
  useEffect(() => {
    const wasHedgeMode = prevHedgeModeRef.current;
    prevHedgeModeRef.current = isHedgeCopyModeActive;

    if (isHedgeCopyModeActive && useOCO && ocoTPOffset !== ocoSLOffset) {
      // Set both to the higher of the two values
      const syncedValue = Math.max(ocoTPOffset, ocoSLOffset);
      setOcoTPOffset(syncedValue);
      setOcoSLOffset(syncedValue);
    } else if (wasHedgeMode && !isHedgeCopyModeActive) {
      // Hedge mode just disabled - reset to defaults
      setOcoTPOffset(5);
      setOcoSLOffset(50);
    }
  }, [isHedgeCopyModeActive, useOCO]);

  // Handle TP/SL changes in hedge mode - keep them in sync
  const handleTPChange = useCallback((newTP: number) => {
    setOcoTPOffset(newTP);
    if (isHedgeCopyModeActive) {
      setOcoSLOffset(newTP);
    }
  }, [isHedgeCopyModeActive]);

  const handleSLChange = useCallback((newSL: number) => {
    setOcoSLOffset(newSL);
    if (isHedgeCopyModeActive) {
      setOcoTPOffset(newSL);
    }
  }, [isHedgeCopyModeActive]);

  // Derived state from active broker
  const isLiveConnected = activeBroker?.status === 'connected';
  const isDemo = activeBroker?.isDemo ?? true;
  const livePositions = activeBroker?.positions ?? [];
  const liveOrders = activeBroker?.orders ?? [];

  const prevMarketStatusRef = useRef<'OPEN' | 'CLOSED'>(marketStatus);

  const spec = getInstrumentSpec(symbol);
  const canTrade = isLiveConnected && isTradeable(spec);
  const isAlwaysOpenMarket = isAlwaysOpen(spec);

  // News proximity warning
  const { nearbyEvents, isNearEvent } = useNewsProximity();
  const [newsConfirmState, setNewsConfirmState] = useState<{
    isOpen: boolean;
    action: 'Buy' | 'Sell' | 'Flatten';
    pendingAction: (() => Promise<void>) | null;
  } | null>(null);

  const checkNewsAndExecute = useCallback(
    (action: 'Buy' | 'Sell' | 'Flatten', handler: () => Promise<void>) => {
      if (isNearEvent) {
        setNewsConfirmState({ isOpen: true, action, pendingAction: handler });
      } else {
        handler();
      }
    },
    [isNearEvent],
  );

  // Watch for market close
  useEffect(() => {
    if (dataMode === 'backtest') return;
    if (prevMarketStatusRef.current === 'OPEN' && marketStatus === 'CLOSED') {
      addLog('Market closed', 'error');
    }
    prevMarketStatusRef.current = marketStatus;
  }, [marketStatus, dataMode]);

  // Track previous position to detect when position closes
  const prevPositionNetPosRef = useRef<number | null>(null);

  // Reset qty to the user's default when position closes (netPos goes from
  // non-zero to zero). This used to hardcode 1, which threw away the size the
  // user had chosen on every single close.
  useEffect(() => {
    const cleanedSymbol = symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '');

    // Find position - must match getExistingPosition logic including fallback
    const position = livePositions.find(pos => {
      if (pos.symbol) {
        const posSymbol = pos.symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '');
        return (posSymbol === cleanedSymbol || posSymbol.startsWith(cleanedSymbol) || cleanedSymbol.startsWith(posSymbol));
      }
      // Fallback: if no symbol, include any position (single-instrument assumption)
      return true;
    });

    // Get current netPos - use the position's actual netPos value
    const currentNetPos = position?.netPos ?? 0;
    const prevNetPos = prevPositionNetPosRef.current;

    // Reset qty when position closes (was non-zero, now zero)
    if (prevNetPos !== null && prevNetPos !== 0 && currentNetPos === 0) {
      console.log(`[TradingPanel] Position closed, resetting qty to default ${defaultQuantity}`);
      setSelectedQuantity(defaultQuantity);
    }

    prevPositionNetPosRef.current = currentNetPos;
  }, [livePositions, symbol, defaultQuantity]);

  // Helper to find existing position for the symbol (memoized). Per account:
  // the order goes to the selected account, so only its position counts.
  const selectedAccountId = activeBroker?.selectedAccountId ?? null;
  const getExistingPosition = useCallback(() => {
    const position = findOpenPosition(livePositions, symbol, selectedAccountId);
    if (position) {
      console.log('[TradingPanel] getExistingPosition result:', { id: position.id, netPos: position.netPos, symbol: position.symbol });
    } else {
      console.log('[TradingPanel] getExistingPosition - no position found');
    }
    return position;
  }, [symbol, livePositions, selectedAccountId]);

  // Restrain: the open position on this symbol (same match as
  // getExistingPosition, without its logging, since this runs every render)
  // and which side would change its size.
  const restrainPos = findOpenPosition(livePositions, symbol, selectedAccountId);
  const restrainTarget = restrainPos
    ? {
        brokerId: restrainPos.brokerId || activeBroker?.id,
        accountId: restrainPos.accountId ?? selectedAccountId,
        symbol: restrainPos.symbol,
        contractId: restrainPos.contractId,
        netPos: restrainPos.netPos,
        fallbackSymbol: symbol,
      }
    : null;
  const restrainLock = useRestrainOrderLock(restrainTarget, selectedQuantity);
  // In a position the opposite side is exit-only: "CLOSE 3" / "EXIT 2 of 3 short";
  // a side restrain blocks reads "LOCKED · restrain 1:52:10" (live countdown).
  // The mobile bar (42px tall) wraps the long ones onto two lines; the desktop
  // panel buttons are one line, so they say "RST".
  const restrainLabelOpts = { restrain: restrainLock.entry, now: restrainLock.now };
  const mobileBuy = manualOrderButton(restrainPos?.netPos, 'Buy', selectedQuantity, restrainLabelOpts);
  const mobileSell = manualOrderButton(restrainPos?.netPos, 'Sell', selectedQuantity, restrainLabelOpts);
  const panelBuy = manualOrderButton(restrainPos?.netPos, 'Buy', selectedQuantity, { ...restrainLabelOpts, compact: true });
  const panelSell = manualOrderButton(restrainPos?.netPos, 'Sell', selectedQuantity, { ...restrainLabelOpts, compact: true });
  /** Long labels (EXIT / LOCKED) get a smaller font so they fit the button. */
  const longLabel = (label: string) => (label.length > 8 ? ' order-btn-long-label' : '');

  // Helper to find TP/SL orders for modifying quantity (memoized)
  // Returns orders that match the position's contractId and direction
  const findTPSLOrders = useCallback((position: typeof livePositions[0] | undefined) => {
    if (!position) {
      console.log('[TradingPanel] findTPSLOrders - no position provided');
      return { tpOrder: undefined, slOrder: undefined };
    }

    const isLong = position.netPos > 0;
    console.log('[TradingPanel] findTPSLOrders - position:', { netPos: position.netPos, isLong, contractId: position.contractId });
    console.log('[TradingPanel] findTPSLOrders - all liveOrders:', liveOrders.length);

    // Working orders have status like 'Working', 'Accepted', 'Pending'
    const workingOrders = liveOrders.filter(o => {
      const isWorking = o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending';
      // Match by contractId if available, otherwise include all working orders
      const matchesContract = !position.contractId || !o.contractId || o.contractId === position.contractId;
      return isWorking && matchesContract;
    });

    console.log('[TradingPanel] findTPSLOrders - working orders:', workingOrders.map(o => ({
      id: o.id, ordType: o.ordType, orderType: (o as any).orderType, action: o.action, qty: o.orderQty, price: o.price, stopPrice: o.stopPrice
    })));

    // For LONG position: TP is Sell Limit, SL is Sell Stop
    // For SHORT position: TP is Buy Limit, SL is Buy Stop
    const tpAction = isLong ? 'Sell' : 'Buy';
    const slAction = isLong ? 'Sell' : 'Buy';

    // Check both ordType and orderType (fallback) since normalization might not have happened yet
    const getOrderType = (o: any) => o.ordType || o.orderType;

    const tpOrder = workingOrders.find(o => {
      const type = getOrderType(o);
      return type === 'Limit' && o.action === tpAction;
    });
    const slOrder = workingOrders.find(o => {
      const type = getOrderType(o);
      return (type === 'Stop' || type === 'StopMarket') && o.action === slAction;
    });

    console.log('[TradingPanel] findTPSLOrders result:', {
      tpOrder: tpOrder ? { id: tpOrder.id, qty: tpOrder.orderQty, price: tpOrder.price } : null,
      slOrder: slOrder ? { id: slOrder.id, qty: slOrder.orderQty, stopPrice: slOrder.stopPrice } : null
    });

    return { tpOrder, slOrder };
  }, [liveOrders]);

  const handleBuy = useCallback(async () => {
    console.log('[TradingPanel] handleBuy - START', { canTrade, isMarketOpen, currentPrice, selectedQuantity, symbol });
    if (!canTrade || !isMarketOpen || currentPrice <= 0) {
      console.log('[TradingPanel] handleBuy - BLOCKED:', { canTrade, isMarketOpen, currentPrice });
      return;
    }

    if (isFullSizeContract(symbol) && !fullSizeConfirmDisabled && !fullSizeBypassRef.current) {
      setFullSizeConfirm({ action: 'Buy' });
      return;
    }
    fullSizeBypassRef.current = false;

    const existingPos = getExistingPosition();
    const hasPosition = existingPos && existingPos.netPos !== 0;
    console.log('[TradingPanel] handleBuy - position state:', { hasPosition, netPos: existingPos?.netPos });

    if (hasPosition) {
      // Has existing position - place market order and modify TP/SL to new position size
      // Buying: adds to long (netPos > 0) or reduces short (netPos < 0)
      const currentNetPos = existingPos.netPos;
      // Opposite side is exit-only: a plain order capped at the position, so it
      // can never reverse. A same-side add is sent as before.
      const orderQty = planManualOrder(currentNetPos, 'Buy', selectedQuantity).quantity;
      const newNetPos = currentNetPos + orderQty; // Buy increases netPos
      const isClosing = newNetPos === 0;
      const isPartialClose = currentNetPos < 0 && newNetPos < 0 && newNetPos > currentNetPos; // Reducing short

      console.log('[TradingPanel] handleBuy - SCALING:', {
        currentNetPos,
        orderQty,
        newNetPos,
        isClosing,
        isPartialClose
      });

      const result = await placeOrder({ action: 'Buy', symbol, quantity: orderQty });
      console.log('[TradingPanel] handleBuy - placeOrder result:', result);

      if (result.success) {
        const newPositionSize = Math.abs(newNetPos);

        if (isClosing) {
          // Position closed - TP/SL cancelled by Tradovate automatically
          console.log('[TradingPanel] handleBuy - CLOSED position');
          addLog(`BUY ${orderQty} (position closed)`, 'success');
        } else if (newPositionSize > 0 && newPositionSize < Math.abs(currentNetPos) && serverSizedLegs(result)) {
          // Partial exit: the service already sized the TP/SL to what is left.
          addLog(`BUY ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL -> ${newPositionSize})`, 'success');
        } else if (newPositionSize > 0) {
          // Still have a position - replace TP/SL with new qty
          // Use replaceBracket because Tradovate doesn't allow modifying Limit order qty

          // Wait a moment for orders to be refreshed after fill
          await new Promise(resolve => setTimeout(resolve, 500));

          // Re-fetch fresh orders from store
          const freshOrders = useBrokerStore.getState().getBroker(useBrokerStore.getState().activeBrokerId || '')?.orders || [];
          const workingOrders = freshOrders.filter((o: any) =>
            (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
          );

          const exitAction = newNetPos > 0 ? 'Sell' : 'Buy';
          const getOrderType = (o: any) => o.ordType || o.orderType;

          const tpOrder = workingOrders.find((o: any) => getOrderType(o) === 'Limit' && o.action === exitAction);
          const slOrder = workingOrders.find((o: any) => (getOrderType(o) === 'Stop' || getOrderType(o) === 'StopMarket') && o.action === exitAction);

          console.log('[TradingPanel] handleBuy - fresh orders check:', {
            totalOrders: freshOrders.length,
            workingOrders: workingOrders.length,
            tpOrder: tpOrder ? { id: tpOrder.id, type: getOrderType(tpOrder), action: tpOrder.action } : null,
            slOrder: slOrder ? { id: slOrder.id, type: getOrderType(slOrder), action: slOrder.action } : null
          });

          if (tpOrder || slOrder) {
            console.log('[TradingPanel] handleBuy - resizing existing TP/SL to qty:', newPositionSize);

            // Adding to a position: respect the EXISTING TP/SL only — resize them to the new total
            // quantity at their current prices. Never introduce a leg that isn't already there
            // (e.g. if the position has no SL, don't add one). replaceBracket only places a leg when
            // its price is provided, so passing undefined for a missing leg leaves it absent.
            const calculatedTpPrice = tpOrder?.price;
            const calculatedSlPrice = slOrder?.stopPrice;

            console.log('[TradingPanel] handleBuy - resize prices:', {
              tpOrder: tpOrder?.id,
              slOrder: slOrder?.id,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });

            const replaceResult = await replaceBracket({
              tpOrderId: tpOrder?.id,
              slOrderId: slOrder?.id,
              symbol,
              action: exitAction,
              newQuantity: newPositionSize,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });
            if (replaceResult.success) {
              useToastStore.getState().addToast({ type: 'success', message: `TP/SL updated to qty ${newPositionSize}` });
            } else {
              console.log('[TradingPanel] handleBuy - replaceBracket failed:', replaceResult.error);
              useToastStore.getState().addToast({ type: 'error', message: `TP/SL sync failed: ${replaceResult.error}` });
            }
          } else {
            console.log('[TradingPanel] handleBuy - no TP/SL orders found. Working orders:', workingOrders.map((o: any) => ({ id: o.id, type: getOrderType(o), action: o.action })));
            useToastStore.getState().addToast({ type: 'warning', message: `No TP/SL found (${workingOrders.length} orders)` });
          }
        }
      } else {
        console.log('[TradingPanel] handleBuy - FAILED:', result.error);
        addLog(`BUY failed: ${result.error}`, 'error');
      }
    } else if (useOCO && (ocoTPEnabled || ocoSLEnabled)) {
      // No existing position - create new bracket order with enabled TP/SL
      const result = await placeBracketOrder({
        action: 'Buy', symbol, quantity: selectedQuantity,
        profitTargetPoints: ocoTPEnabled ? ocoTPOffset : undefined,
        stopLossPoints: ocoSLEnabled ? -ocoSLOffset : undefined,
      });
      const parts = [ocoTPEnabled && 'TP', ocoSLEnabled && 'SL'].filter(Boolean).join('/');
      if (result.success) addLog(`BUY ${selectedQuantity} +${parts}`, 'success');
      else addLog(`BUY failed: ${result.error}`, 'error');
    } else {
      // No existing position, no OCO - simple market order
      const result = await placeOrder({ action: 'Buy', symbol, quantity: selectedQuantity });
      if (result.success) addLog(`BUY ${selectedQuantity}`, 'success');
      else addLog(`BUY failed: ${result.error}`, 'error');
    }
  }, [canTrade, isMarketOpen, currentPrice, getExistingPosition, selectedQuantity, placeOrder, symbol, findTPSLOrders, replaceBracket, useOCO, ocoTPEnabled, ocoSLEnabled, placeBracketOrder, ocoTPOffset, ocoSLOffset]);

  const handleSell = useCallback(async () => {
    console.log('[TradingPanel] handleSell - START', { canTrade, isMarketOpen, currentPrice, selectedQuantity, symbol });
    if (!canTrade || !isMarketOpen || currentPrice <= 0) {
      console.log('[TradingPanel] handleSell - BLOCKED:', { canTrade, isMarketOpen, currentPrice });
      return;
    }

    if (isFullSizeContract(symbol) && !fullSizeConfirmDisabled && !fullSizeBypassRef.current) {
      setFullSizeConfirm({ action: 'Sell' });
      return;
    }
    fullSizeBypassRef.current = false;

    const existingPos = getExistingPosition();
    const hasPosition = existingPos && existingPos.netPos !== 0;
    console.log('[TradingPanel] handleSell - position state:', { hasPosition, netPos: existingPos?.netPos });

    if (hasPosition) {
      // Has existing position - place market order and modify TP/SL to new position size
      // Selling: reduces long (netPos > 0) or adds to short (netPos < 0)
      const currentNetPos = existingPos.netPos;
      // Opposite side is exit-only: a plain order capped at the position, so it
      // can never reverse. A same-side add is sent as before.
      const orderQty = planManualOrder(currentNetPos, 'Sell', selectedQuantity).quantity;
      const newNetPos = currentNetPos - orderQty; // Sell decreases netPos
      const isClosing = newNetPos === 0;
      const isPartialClose = currentNetPos > 0 && newNetPos > 0 && newNetPos < currentNetPos; // Reducing long

      console.log('[TradingPanel] handleSell - SCALING:', {
        currentNetPos,
        orderQty,
        newNetPos,
        isClosing,
        isPartialClose
      });

      const result = await placeOrder({ action: 'Sell', symbol, quantity: orderQty });
      console.log('[TradingPanel] handleSell - placeOrder result:', result);

      if (result.success) {
        const newPositionSize = Math.abs(newNetPos);

        if (isClosing) {
          // Position closed - TP/SL cancelled by Tradovate automatically
          console.log('[TradingPanel] handleSell - CLOSED position');
          addLog(`SELL ${orderQty} (position closed)`, 'success');
        } else if (newPositionSize > 0 && newPositionSize < Math.abs(currentNetPos) && serverSizedLegs(result)) {
          // Partial exit: the service already sized the TP/SL to what is left.
          addLog(`SELL ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL -> ${newPositionSize})`, 'success');
        } else if (newPositionSize > 0) {
          // Still have a position - replace TP/SL with new qty
          // Use replaceBracket because Tradovate doesn't allow modifying Limit order qty

          // Wait a moment for orders to be refreshed after fill
          await new Promise(resolve => setTimeout(resolve, 500));

          // Re-fetch fresh orders from store
          const freshOrders = useBrokerStore.getState().getBroker(useBrokerStore.getState().activeBrokerId || '')?.orders || [];
          const workingOrders = freshOrders.filter((o: any) =>
            (o.ordStatus === 'Working' || o.ordStatus === 'Accepted' || o.ordStatus === 'Pending')
          );

          const exitAction = newNetPos > 0 ? 'Sell' : 'Buy';
          const getOrderType = (o: any) => o.ordType || o.orderType;

          const tpOrder = workingOrders.find((o: any) => getOrderType(o) === 'Limit' && o.action === exitAction);
          const slOrder = workingOrders.find((o: any) => (getOrderType(o) === 'Stop' || getOrderType(o) === 'StopMarket') && o.action === exitAction);

          console.log('[TradingPanel] handleSell - fresh orders check:', {
            totalOrders: freshOrders.length,
            workingOrders: workingOrders.length,
            tpOrder: tpOrder ? { id: tpOrder.id, type: getOrderType(tpOrder), action: tpOrder.action } : null,
            slOrder: slOrder ? { id: slOrder.id, type: getOrderType(slOrder), action: slOrder.action } : null
          });

          if (tpOrder || slOrder) {
            console.log('[TradingPanel] handleSell - resizing existing TP/SL to qty:', newPositionSize);

            // Adding to a position: respect the EXISTING TP/SL only — resize them to the new total
            // quantity at their current prices. Never introduce a leg that isn't already there
            // (e.g. if the position has no SL, don't add one). replaceBracket only places a leg when
            // its price is provided, so passing undefined for a missing leg leaves it absent.
            const calculatedTpPrice = tpOrder?.price;
            const calculatedSlPrice = slOrder?.stopPrice;

            console.log('[TradingPanel] handleSell - resize prices:', {
              tpOrder: tpOrder?.id,
              slOrder: slOrder?.id,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });

            const replaceResult = await replaceBracket({
              tpOrderId: tpOrder?.id,
              slOrderId: slOrder?.id,
              symbol,
              action: exitAction,
              newQuantity: newPositionSize,
              tpPrice: calculatedTpPrice,
              slPrice: calculatedSlPrice,
            });
            if (replaceResult.success) {
              addLog(`SELL ${orderQty} (pos: ${newNetPos > 0 ? '+' : ''}${newNetPos}, TP/SL → ${newPositionSize})`, 'success');
            } else {
              console.log('[TradingPanel] handleSell - replaceBracket failed:', replaceResult.error);
              addLog(`SELL ${orderQty} (TP/SL sync failed: ${replaceResult.error})`, 'warning');
            }
          } else {
            console.log('[TradingPanel] handleSell - no TP/SL orders found. Working orders:', workingOrders.map((o: any) => ({ id: o.id, type: getOrderType(o), action: o.action })));
            addLog(`SELL ${orderQty} (pos: ${newNetPos}, no TP/SL found - ${workingOrders.length} orders)`, 'warning');
          }
        }
      } else {
        console.log('[TradingPanel] handleSell - FAILED:', result.error);
        addLog(`SELL failed: ${result.error}`, 'error');
      }
    } else if (useOCO && (ocoTPEnabled || ocoSLEnabled)) {
      // No existing position - create new bracket order with enabled TP/SL
      const result = await placeBracketOrder({
        action: 'Sell', symbol, quantity: selectedQuantity,
        profitTargetPoints: ocoTPEnabled ? -ocoTPOffset : undefined,
        stopLossPoints: ocoSLEnabled ? ocoSLOffset : undefined,
      });
      const parts = [ocoTPEnabled && 'TP', ocoSLEnabled && 'SL'].filter(Boolean).join('/');
      if (result.success) addLog(`SELL ${selectedQuantity} +${parts}`, 'success');
      else addLog(`SELL failed: ${result.error}`, 'error');
    } else {
      // No existing position, no OCO - simple market order
      const result = await placeOrder({ action: 'Sell', symbol, quantity: selectedQuantity });
      if (result.success) addLog(`SELL ${selectedQuantity}`, 'success');
      else addLog(`SELL failed: ${result.error}`, 'error');
    }
  }, [canTrade, isMarketOpen, currentPrice, getExistingPosition, selectedQuantity, placeOrder, symbol, findTPSLOrders, replaceBracket, useOCO, ocoTPEnabled, ocoSLEnabled, placeBracketOrder, ocoTPOffset, ocoSLOffset]);

  const handleFlatten = useCallback(async () => {
    console.log('[TradingPanel] handleFlatten - START', { canTrade, symbol });
    if (!canTrade) {
      console.log('[TradingPanel] handleFlatten - BLOCKED: cannot trade');
      return;
    }
    const existingPos = getExistingPosition();
    console.log('[TradingPanel] handleFlatten - current position:', existingPos ? { netPos: existingPos.netPos, symbol: existingPos.symbol } : 'none');

    const result = await flattenPosition(symbol);
    console.log('[TradingPanel] handleFlatten - result:', result);

    if (result.success) {
      const posInfo = existingPos ? ` (was ${existingPos.netPos > 0 ? '+' : ''}${existingPos.netPos})` : '';
      addLog(`Flattened${posInfo}`, 'success');
      addToast('success', `Position closed${posInfo}`);
      // Back to the user's default order size after closing the position
      setSelectedQuantity(defaultQuantity);
    } else {
      addLog(`Flatten failed: ${result.error}`, 'error');
      addToast('error', `Flatten failed: ${result.error}`);
    }
    console.log('[TradingPanel] handleFlatten - END');
  }, [canTrade, flattenPosition, symbol, getExistingPosition, defaultQuantity]);

  // Save preferences to server (only after initial sync to avoid overwriting server values)
  // Settings (shared between mobile and desktop)
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { useOCO }); }, [useOCO, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoTPEnabled }); }, [ocoTPEnabled, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoSLEnabled }); }, [ocoSLEnabled, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoTPOffset }); }, [ocoTPOffset, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { ocoSLOffset }); }, [ocoSLOffset, updatePreference]);
  useEffect(() => { if (hasSyncedPrefs.current) updatePreference('tradingPanel', { quantity: selectedQuantity }); }, [selectedQuantity, updatePreference]);
  // Size (separate for mobile and desktop)
  useEffect(() => {
    if (hasSyncedPrefs.current) {
      if (isMobile) {
        updatePreference('tradingPanel', { mobileSize: size });
      } else {
        updatePreference('tradingPanel', { size });
      }
    }
  }, [size, updatePreference, isMobile]);

  // Resize handlers (memoized)
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);
    const rect = panelRef.current?.getBoundingClientRect();
    if (rect) {
      resizeStartRef.current = { x: e.clientX, y: e.clientY, width: rect.width, height: rect.height };
    }
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    const handleResizeMove = (e: MouseEvent) => {
      if (!resizeStartRef.current) return;
      const deltaX = e.clientX - resizeStartRef.current.x;
      const newWidth = Math.max(100, Math.min(resizeStartRef.current.width + deltaX, 200));
      setSize({ width: newWidth, height: 0 });
    };
    const handleResizeEnd = () => {
      setIsResizing(false);
      resizeStartRef.current = null;
    };
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing]);

  // Preset chips, with the user's default folded in so a non-stock default
  // (say 4) still renders and still highlights. Shared with desktop.
  const quantities = quantityPresets;

  // ---------------------------------------------------------------------------
  // Publish the mobile bar's real height so the chart can reserve space for it.
  //
  // The bar wraps to two rows and adds env(safe-area-inset-bottom), so its
  // height depends on viewport width, which chips are shown, and the device.
  // Any hardcoded number goes stale the moment one of those changes — which is
  // exactly how the chart ended up reserving 44px for a 104px bar. Measure it
  // instead and hand the number to CSS as --mobile-trading-bar-h.
  // ---------------------------------------------------------------------------
  const mobileBarObserverRef = useRef<ResizeObserver | null>(null);

  const releaseMobileBarHeight = useCallback(() => {
    mobileBarObserverRef.current?.disconnect();
    mobileBarObserverRef.current = null;
    document.documentElement.style.removeProperty(MOBILE_BAR_HEIGHT_VAR);
  }, []);

  // Callback ref rather than an effect: this fires exactly when the element
  // attaches/detaches, so it can't drift out of sync with a dependency array.
  const mobileBarRef = useCallback((el: HTMLDivElement | null) => {
    releaseMobileBarHeight();
    // No element (unmounted, or the panel was hidden) — clear it so the chart
    // falls back to reserving nothing but the x-axis allowance.
    if (!el) return;

    const publish = (): void => {
      // getBoundingClientRect covers padding, border and the safe-area inset,
      // and reports 0 when the bar is display:none (i.e. above 768px), which
      // is the signal we want for "reserve nothing".
      const height = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty(MOBILE_BAR_HEIGHT_VAR, `${height}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    mobileBarObserverRef.current = observer;
  }, [releaseMobileBarHeight]);

  // Belt-and-braces: React nulls the callback ref on unmount, but if the whole
  // panel is torn down mid-render we still want the variable gone.
  useEffect(() => releaseMobileBarHeight, [releaseMobileBarHeight]);

  // Mobile state for expanded settings panel.
  //
  // MUST stay above the `!enabled` return. It used to sit below it, which meant
  // this component rendered a different NUMBER of hooks depending on `enabled` —
  // React throws "Rendered more hooks than during the previous render" the first
  // time that flag flips, and the ChartView error boundary eats the whole chart.
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);

  if (!enabled) return null;

  // Mobile bottom bar version - TradingView-inspired with expandable settings
  if (isMobile) {
    return (
      <>
        {/* Expandable Settings Panel */}
        {mobileSettingsOpen && (
          <div className="mtb-settings-panel">
            <div className="mtb-settings-header">
              <span>Order Settings</span>
              <button className="mtb-settings-close" onClick={() => setMobileSettingsOpen(false)}>×</button>
            </div>

            {/* Hedge Mode Indicator */}
            {isHedgeCopyModeActive && (
              <div className="mtb-hedge-banner">
                <Shield size={14} />
                <span>HEDGE MODE - TP=SL enforced</span>
              </div>
            )}

            <div className="mtb-settings-row">
              <span className="mtb-settings-label">OCO Bracket</span>
              <button
                className={`mtb-oco-toggle ${useOCO ? 'active' : ''}`}
                onClick={() => setUseOCO(!useOCO)}
              >
                {useOCO ? 'ON' : 'OFF'}
              </button>
            </div>
            {useOCO && (
              <>
                {isHedgeCopyModeActive ? (
                  /* Hedge mode: single unified TP/SL input */
                  <div className="mtb-settings-row">
                    <span className="mtb-settings-label">TP / SL (pts)</span>
                    <div className="mtb-input-group">
                      <button className="mtb-input-btn" onClick={() => handleTPChange(Math.max(0.25, ocoTPOffset - 0.25))}>−</button>
                      <input
                        type="number"
                        className="mtb-input"
                        value={ocoTPOffset}
                        onChange={(e) => handleTPChange(Number(e.target.value))}
                        step="0.25"
                        min="0.25"
                      />
                      <button className="mtb-input-btn" onClick={() => handleTPChange(ocoTPOffset + 0.25)}>+</button>
                    </div>
                  </div>
                ) : (
                  /* Normal mode: separate TP and SL inputs with enable checkboxes */
                  <>
                    <div className={`mtb-settings-row ${!ocoTPEnabled ? 'disabled' : ''}`}>
                      <label className="mtb-settings-label mtb-checkbox-label">
                        <input
                          type="checkbox"
                          checked={ocoTPEnabled}
                          onChange={(e) => setOcoTPEnabled(e.target.checked)}
                          className="mtb-checkbox"
                        />
                        TP (pts)
                      </label>
                      <div className="mtb-input-group">
                        <button className="mtb-input-btn" onClick={() => setOcoTPOffset(Math.max(0.25, ocoTPOffset - 0.25))} disabled={!ocoTPEnabled}>−</button>
                        <input
                          type="number"
                          className="mtb-input"
                          value={ocoTPOffset}
                          onChange={(e) => setOcoTPOffset(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                          disabled={!ocoTPEnabled}
                        />
                        <button className="mtb-input-btn" onClick={() => setOcoTPOffset(ocoTPOffset + 0.25)} disabled={!ocoTPEnabled}>+</button>
                      </div>
                    </div>
                    <div className={`mtb-settings-row ${!ocoSLEnabled ? 'disabled' : ''}`}>
                      <label className="mtb-settings-label mtb-checkbox-label">
                        <input
                          type="checkbox"
                          checked={ocoSLEnabled}
                          onChange={(e) => setOcoSLEnabled(e.target.checked)}
                          className="mtb-checkbox"
                        />
                        SL (pts)
                      </label>
                      <div className="mtb-input-group">
                        <button className="mtb-input-btn" onClick={() => setOcoSLOffset(Math.max(0.25, ocoSLOffset - 0.25))} disabled={!ocoSLEnabled}>−</button>
                        <input
                          type="number"
                          className="mtb-input"
                          value={ocoSLOffset}
                          onChange={(e) => setOcoSLOffset(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                          disabled={!ocoSLEnabled}
                        />
                        <button className="mtb-input-btn" onClick={() => setOcoSLOffset(ocoSLOffset + 0.25)} disabled={!ocoSLEnabled}>+</button>
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
            <div className="mtb-settings-row">
              <span className="mtb-settings-label">Quantity</span>
              <div className="mtb-qty-selector">
                {quantities.map((q) => (
                  <button
                    key={q}
                    className={`mtb-qty-opt ${selectedQuantity === q ? 'active' : ''}`}
                    onClick={() => setSelectedQuantity(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bottom Trading Bar */}
        <div className="mobile-trading-bar" ref={mobileBarRef}>
          {/* Rainbow border at the top edge of the trading bar */}
          <AnimatedRainbowBorder placement="top" />
          {/* Trading Lock Button - reuses shared component */}
          <TradingLockButton
            isDisciplineLocked={isDisciplineLocked}
            lockMode={disciplineLockMode}
            lockExpiresAt={disciplineLockExpiresAt}
            lockDropdownOpen={lockDropdownOpen}
            setLockDropdownOpen={setLockDropdownOpen}
            startCoolOff={startCoolOff}
            startCoolOffUntil={startCoolOffUntil}
            setDoneForDay={setDoneForDay}
            openUnlockModal={openUnlockModal}
            openSettingsModal={openSettingsModal}
            simpleUnlock={simpleUnlock}
            simpleUnlockEnabled={simpleUnlockEnabled}
            activeBroker={activeBroker}
          />

          {/* Settings toggle */}
          <button
            className={`mtb-settings-btn ${mobileSettingsOpen ? 'active' : ''} ${useOCO ? 'oco-on' : ''}`}
            onClick={() => setMobileSettingsOpen(!mobileSettingsOpen)}
            title="Order settings"
          >
            ⚙
          </button>

          {/* Full-size toggle - only on NQ/ES/YM/RTY */}
          {isFullSizeContract(symbol) && (
            <button
              className={`mtb-fullsize-toggle ${fullSizeConfirmDisabled ? 'active' : ''}`}
              onClick={() => setFullSizeConfirmDisabled(!fullSizeConfirmDisabled)}
            >
              {fullSizeConfirmDisabled ? 'FS:ON' : 'FS:OFF'}
            </button>
          )}

          {/* Schedule + arm, on mobile too. ActionBarTrading (which carries
              these on desktop) returns null here, so mobile needs them wired
              explicitly or the features simply don't exist on phone. */}
          <ScheduleSelector />
          <RestrainToggle className="mtb-restrain" />
          <button
            className={`mtb-arm ${botsArmed ? 'armed' : ''}`}
            onClick={() => useAutoBotStore.getState().setGlobalEnabled(!botsArmed)}
            title={botsArmed ? 'Bots ARMED - tap to disarm' : 'Bots disarmed - tap to arm'}
          >
            {botsArmed ? 'ARM' : 'OFF'}
          </button>

          {/* Action buttons - center */}
          <button
            className={`mtb-buy${restrainLock.buyLock ? ' restrain-locked' : ''}${longLabel(mobileBuy.label)}`}
            onClick={() => scheduleOrRun('Buy', () => checkNewsAndExecute('Buy', handleBuy))}
            disabled={!canTrade || (!isMarketOpen && !isAlwaysOpenMarket) || !!restrainLock.buyLock}
            title={mobileBuy.title}
          >
            <span className="mtb-btn-action">{mobileBuy.label}</span>
          </button>
          {/* Ad-hoc working order ticket */}
          <button
            className="mtb-ticket-btn"
            onClick={() => window.dispatchEvent(new CustomEvent('mobile-show-order-preview'))}
            disabled={!canTrade || (!isMarketOpen && !isAlwaysOpenMarket) || !currentPrice}
            title="Ad-hoc working order"
          >
            TKT
          </button>
          <button
            className={`mtb-sell${restrainLock.sellLock ? ' restrain-locked' : ''}${longLabel(mobileSell.label)}`}
            onClick={() => scheduleOrRun('Sell', () => checkNewsAndExecute('Sell', handleSell))}
            disabled={!canTrade || (!isMarketOpen && !isAlwaysOpenMarket) || !!restrainLock.sellLock}
            title={mobileSell.title}
          >
            <span className="mtb-btn-action">{mobileSell.label}</span>
          </button>

          {/* Quantity display with +/- buttons */}
          <div className="mtb-qty-display">
            <button
              className="mtb-qty-btn"
              onClick={() => setSelectedQuantity(clampOrderQuantity(selectedQuantity - 1, selectedQuantity))}
            >
              -
            </button>
            <span className="mtb-qty-value">{selectedQuantity}</span>
            <button
              className="mtb-qty-btn"
              onClick={() => setSelectedQuantity(clampOrderQuantity(selectedQuantity + 1, selectedQuantity))}
            >
              +
            </button>
          </div>

          {/* OCO badge - two lines for TP/SL */}
          {useOCO && (
            <div className={`mtb-oco-badge ${isHedgeCopyModeActive ? 'hedge' : ''}`}>
              {isHedgeCopyModeActive ? (
                <span>HEDGE {ocoTPOffset}</span>
              ) : (
                <>
                  <span className={`mtb-oco-tp ${!ocoTPEnabled ? 'disabled' : ''}`}>TP {ocoTPEnabled ? ocoTPOffset : '-'}</span>
                  <span className={`mtb-oco-sl ${!ocoSLEnabled ? 'disabled' : ''}`}>SL {ocoSLEnabled ? ocoSLOffset : '-'}</span>
                </>
              )}
            </div>
          )}
        </div>

        {/* News event confirmation modal (mobile) */}
        <NewsOrderConfirmModal
          isOpen={newsConfirmState?.isOpen ?? false}
          events={nearbyEvents}
          orderAction={newsConfirmState?.action ?? 'Buy'}
          onConfirm={() => {
            newsConfirmState?.pendingAction?.();
            setNewsConfirmState(null);
          }}
          onCancel={() => setNewsConfirmState(null)}
        />
        <FullSizeContractConfirmModal
          isOpen={!!fullSizeConfirm}
          symbol={symbol}
          action={fullSizeConfirm?.action || 'Buy'}
          quantity={selectedQuantity}
          onConfirm={() => {
            const action = fullSizeConfirm?.action;
            setFullSizeConfirm(null);
            fullSizeBypassRef.current = true;
            if (action === 'Buy') handleBuy();
            else if (action === 'Sell') handleSell();
          }}
          onCancel={() => {
            addLog(`${fullSizeConfirm?.action} cancelled - full-size declined`, 'warning');
            setFullSizeConfirm(null);
          }}
        />
      </>
    );
  }

  // Desktop version - Modern glassmorphism design
  return (
    <div
      ref={panelRef}
      className={`trading-panel-v2 ${isCollapsed ? 'collapsed' : ''} ${isDragging ? 'dragging' : ''} ${isResizing ? 'resizing' : ''} ${canTrade ? 'connected' : ''}`}
      style={{ left: `${position.x}px`, top: `${position.y}px`, width: isCollapsed ? 'auto' : `${size.width}px` }}
      {...dragHandlers}
    >
      {/* Header */}
      <div className="tp-header">
        <div className="tp-header-left">
          <div className={`tp-status-indicator ${canTrade ? (isDemo ? 'demo' : 'live') : 'off'}`} />
          <span className={`tp-status ${canTrade ? (isDemo ? 'demo' : 'live') : 'off'}`}>
            {canTrade ? (isDemo ? 'DEMO' : 'LIVE') : 'OFF'}
          </span>
          {/* Symbol indicator */}
          <span className="tp-symbol-badge" title={symbol}>
            {symbol.replace(/\.(CME|CBOT|NYMEX|COMEX|NYBOT|KCBT|MGE)$/i, '')}
          </span>

          {/* Trading Lock Button - reuses shared component */}
          <TradingLockButton
            isDisciplineLocked={isDisciplineLocked}
            lockMode={disciplineLockMode}
            lockExpiresAt={disciplineLockExpiresAt}
            lockDropdownOpen={lockDropdownOpen}
            setLockDropdownOpen={setLockDropdownOpen}
            startCoolOff={startCoolOff}
            startCoolOffUntil={startCoolOffUntil}
            setDoneForDay={setDoneForDay}
            openUnlockModal={openUnlockModal}
            openSettingsModal={openSettingsModal}
            simpleUnlock={simpleUnlock}
            simpleUnlockEnabled={simpleUnlockEnabled}
            activeBroker={activeBroker}
          />
        </div>
        <button className="tp-collapse" onClick={() => setIsCollapsed(!isCollapsed)}>
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {!isCollapsed && (
        <div className="tp-body">
          {/* Market closed warning */}
          {!isMarketOpen && (
            <div className="tp-closed">
              <X size={10} />
              <span>MARKET CLOSED</span>
            </div>
          )}

          {/* Quantity Section */}
          <div className="tp-section">
            <div className="tp-section-label">Qty</div>
            <div className="tp-qty-row">
              <button
                className="tp-qty-btn"
                onClick={() => setSelectedQuantity(clampOrderQuantity(selectedQuantity - 1, selectedQuantity))}
              >
                <Minus size={10} />
              </button>
              <div className="tp-qty-chips">
                {quantities.map((q) => (
                  <button
                    key={q}
                    className={`tp-qty ${selectedQuantity === q ? 'active' : ''}`}
                    onClick={() => setSelectedQuantity(q)}
                  >
                    {q}
                  </button>
                ))}
              </div>
              <button
                className="tp-qty-btn"
                onClick={() => setSelectedQuantity(clampOrderQuantity(selectedQuantity + 1, selectedQuantity))}
              >
                <Plus size={10} />
              </button>
            </div>
          </div>

          {/* Hedge Mode Indicator (Desktop) */}
          {isHedgeCopyModeActive && (
            <div className="tp-hedge-banner">
              <Shield size={10} />
              <span>HEDGE TP=SL</span>
            </div>
          )}

          {/* OCO Section */}
          <div className="tp-section">
            <div className="tp-oco-header">
              <button
                className={`tp-oco-toggle ${useOCO ? 'on' : ''} ${isHedgeCopyModeActive ? 'hedge' : ''}`}
                onClick={() => setUseOCO(!useOCO)}
              >
                {isHedgeCopyModeActive ? <Shield size={9} /> : <Zap size={9} />}
                <span>{isHedgeCopyModeActive ? 'HEDGE' : 'OCO'}</span>
                <div className={`tp-toggle-switch ${useOCO ? 'on' : ''}`}>
                  <div className="tp-toggle-knob" />
                </div>
              </button>
            </div>
            {useOCO && (
              <div className="tp-oco-inputs">
                {isHedgeCopyModeActive ? (
                  /* Hedge mode: single unified TP/SL input */
                  <div className="tp-oco-input full-width">
                    <span className="tp-oco-label hedge">TP/SL</span>
                    <div className="tp-input-wrapper">
                      <button className="tp-input-btn" onClick={() => handleTPChange(Math.max(0.25, ocoTPOffset - 0.25))}>
                        <Minus size={8} />
                      </button>
                      <input
                        type="number"
                        value={ocoTPOffset}
                        onChange={(e) => handleTPChange(Number(e.target.value))}
                        step="0.25"
                        min="0.25"
                      />
                      <button className="tp-input-btn" onClick={() => handleTPChange(ocoTPOffset + 0.25)}>
                        <Plus size={8} />
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Normal mode: separate TP and SL inputs */
                  <>
                    <div className="tp-oco-input">
                      <span className="tp-oco-label">TP</span>
                      <div className="tp-input-wrapper">
                        <button className="tp-input-btn" onClick={() => setOcoTPOffset(Math.max(0.25, ocoTPOffset - 0.25))}>
                          <Minus size={8} />
                        </button>
                        <input
                          type="number"
                          value={ocoTPOffset}
                          onChange={(e) => setOcoTPOffset(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                        />
                        <button className="tp-input-btn" onClick={() => setOcoTPOffset(ocoTPOffset + 0.25)}>
                          <Plus size={8} />
                        </button>
                      </div>
                    </div>
                    <div className="tp-oco-input">
                      <span className="tp-oco-label sl">SL</span>
                      <div className="tp-input-wrapper">
                        <button className="tp-input-btn" onClick={() => setOcoSLOffset(Math.max(0.25, ocoSLOffset - 0.25))}>
                          <Minus size={8} />
                        </button>
                        <input
                          type="number"
                          value={ocoSLOffset}
                          onChange={(e) => setOcoSLOffset(Number(e.target.value))}
                          step="0.25"
                          min="0.25"
                        />
                        <button className="tp-input-btn" onClick={() => setOcoSLOffset(ocoSLOffset + 0.25)}>
                          <Plus size={8} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="tp-actions">
            <button
              className={`tp-buy${restrainLock.buyLock ? ' restrain-locked' : ''}${longLabel(panelBuy.label)}`}
              onClick={() => checkNewsAndExecute('Buy', handleBuy)}
              disabled={!canTrade || (!isMarketOpen && !isAlwaysOpenMarket) || !!restrainLock.buyLock}
              title={panelBuy.title}
            >
              <TrendingUp size={11} />
              <span>{panelBuy.label}</span>
            </button>
            <button
              className={`tp-sell${restrainLock.sellLock ? ' restrain-locked' : ''}${longLabel(panelSell.label)}`}
              onClick={() => checkNewsAndExecute('Sell', handleSell)}
              disabled={!canTrade || (!isMarketOpen && !isAlwaysOpenMarket) || !!restrainLock.sellLock}
              title={panelSell.title}
            >
              <TrendingDown size={11} />
              <span>{panelSell.label}</span>
            </button>
          </div>

          {!isTradeable(spec) && (
            <div className="tp-crypto-banner" style={{ fontSize: '9px', color: '#f0b90b', textAlign: 'center', padding: '2px 4px', opacity: 0.85 }}>
              Chart Only — Trading not available
            </div>
          )}

          {/* Flatten */}
          <button className="tp-flatten" onClick={() => checkNewsAndExecute('Flatten', handleFlatten)} disabled={!canTrade}>
            <X size={10} />
            <span>FLAT</span>
          </button>
        </div>
      )}

      {/* Resize handle */}
      {!isCollapsed && (
        <div className="tp-resize-handle" onMouseDown={handleResizeStart} title="Drag to resize" />
      )}

      {/* News event confirmation modal */}
      <NewsOrderConfirmModal
        isOpen={newsConfirmState?.isOpen ?? false}
        events={nearbyEvents}
        orderAction={newsConfirmState?.action ?? 'Buy'}
        onConfirm={() => {
          newsConfirmState?.pendingAction?.();
          setNewsConfirmState(null);
        }}
        onCancel={() => setNewsConfirmState(null)}
      />
      <FullSizeContractConfirmModal
        isOpen={!!fullSizeConfirm}
        symbol={symbol}
        action={fullSizeConfirm?.action || 'Buy'}
        quantity={selectedQuantity}
        onConfirm={() => {
          const action = fullSizeConfirm?.action;
          setFullSizeConfirm(null);
          fullSizeBypassRef.current = true;
          if (action === 'Buy') handleBuy();
          else if (action === 'Sell') handleSell();
        }}
        onCancel={() => {
          addLog(`${fullSizeConfirm?.action} cancelled - full-size declined`, 'warning');
          setFullSizeConfirm(null);
        }}
      />
    </div>
  );
};

export default TradingPanel;
